import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { gamesApi } from '../../api/games.api';
import { Plus, Edit, Key, Gamepad2, Settings } from 'lucide-react';
import toast from 'react-hot-toast';

export const GamesList: React.FC = () => {
  const navigate = useNavigate();
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['games'],
    queryFn: gamesApi.getGames,
  });

  const handleRegenerateKey = async (gameId: string) => {
    try {
      setRegeneratingKey(gameId);
      const response = await gamesApi.regenerateApiKey(gameId);
      toast.success('API key regenerated successfully');
      
      // Copy to clipboard
      navigator.clipboard.writeText(response.data.apiKey);
      toast.success('New API key copied to clipboard');
    } catch (error) {
      toast.error('Failed to regenerate API key');
    } finally {
      setRegeneratingKey(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading games..." />;
  }

  const games = data?.data || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Games</h1>
          <p className="text-gray-600 mt-1">Manage your games and their configurations</p>
        </div>
        <Button onClick={() => navigate('/games/create')} icon={<Plus className="w-4 h-4" />}>
          Create Game
        </Button>
      </div>

      {games.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Gamepad2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No games yet</h3>
            <p className="text-gray-600 mb-6">Get started by creating your first game</p>
            <Button onClick={() => navigate('/games/create')} icon={<Plus className="w-4 h-4" />}>
              Create Game
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-6">
          {games.map((game) => (
            <Card key={game._id}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{game.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">Bundle ID: {game.bundleId}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <span className="text-xs text-gray-500">
                      Currency: {game.settings.defaultCurrency}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/games/${game._id}/edit`)}
                    icon={<Edit className="w-4 h-4" />}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRegenerateKey(game._id)}
                    isLoading={regeneratingKey === game._id}
                    icon={<Key className="w-4 h-4" />}
                  >
                    Regenerate Key
                  </Button>
                  {/* <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/adjust-config/${game._id}`)}
                    icon={<Settings className="w-4 h-4" />}
                  >
                    Adjust Config
                  </Button> */}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};